import React from 'react';
import { DestinationSteps } from 'blocks/AutomationOptions/types';
import {
  DESTINATION_PLATFORMS,
  DESTINATION_PRESETS,
  MAX_DURATION_VALID_KEYS,
  MAX_PLATFORM_DURATION_AS_SECS,
  PLATFORMS_CONFIG,
} from 'blocks/DestinationPlatforms/constants';
import { AUDIOGRAM_PLATFORMS_DESTINATION_CONFIG } from 'constants/audiogram';
import {
  AspectRatioName,
  AudiogramPlatformConfig,
  VideoTypes,
  VideoTypeWizardSettings,
} from 'types';
import bem from 'utils/bem';
import { millisToMinutes, millisToSec } from 'utils/time';
import { DisabledPlatformsMap } from './DestinationPlatformsContents';
import {
  DestinationPlatform,
  DestinationPreset,
  DestinationPresetKey,
  DestinationPresetVariation,
  MaxDurationKey,
  PlatformConfig,
  PlatformVideoType,
  VideoTypeConfig,
} from './types';
import VideoTypeIcon from './VideoTypeIcon';

export const block = bem('destination-platforms');

export function checkIfDestinationPresetExists(
  platformName: DestinationPlatform,
): boolean {
  return platformName in DESTINATION_PRESETS;
}

/**
 * Checks if any of the destination platform's presets supports the given
 * aspect ratio
 */
function platformSupportsAspectRatio(
  platform: DestinationPlatform,
  variation: DestinationPresetVariation,
  aspectRatio: AspectRatioName,
): boolean {
  return getDestinationPresets(platform).some(
    preset => preset.aspectRatio?.[variation] === aspectRatio,
  );
}

/**
 * Builds a map of platforms that don't support the required aspect ratio under
 * the given variation. Each entry carries a tooltip string produced by the
 * caller — copy lives at the call site, not in the utility.
 */
export function getDisabledPlatformsForAspectRatio(
  variation: DestinationPresetVariation,
  requiredAspectRatio: AspectRatioName,
  getTooltip: (platform: DestinationPlatform) => string,
): DisabledPlatformsMap {
  return DESTINATION_PLATFORMS.reduce<DisabledPlatformsMap>((acc, platform) => {
    if (
      !platformSupportsAspectRatio(platform, variation, requiredAspectRatio)
    ) {
      acc[platform] = { tooltip: getTooltip(platform) };
    }
    return acc;
  }, {});
}

/**
 * Returns a copy of `videoTypes` where each entry whose aspect ratio under
 * `variation` doesn't match `requiredAspectRatio` is flagged as disabled with
 * the supplied tooltip. Entries that already match (or have no declared ratio)
 * are returned unchanged.
 */
export function decorateVideoTypesForAspectRatio(
  videoTypes: VideoTypeConfig[],
  platform: DestinationPlatform,
  variation: DestinationPresetVariation,
  requiredAspectRatio: AspectRatioName,
  tooltip: string,
): VideoTypeConfig[] {
  return videoTypes.map(videoType => {
    const videoTypeAspectRatio =
      DESTINATION_PRESETS?.[platform]?.[videoType.id]?.aspectRatio?.[variation];

    if (videoTypeAspectRatio && videoTypeAspectRatio !== requiredAspectRatio) {
      return { ...videoType, disabled: true, disabledTooltip: tooltip };
    }

    return videoType;
  });
}

export function getPresetMaxDurationKey(
  selectedPreset: DestinationPreset['key'],
): MaxDurationKey {
  return MAX_DURATION_VALID_KEYS.includes(selectedPreset as MaxDurationKey)
    ? (selectedPreset as MaxDurationKey)
    : 'not set';
}

export function getPlatformInfo(
  platform: DestinationPlatform,
  videoType?: PlatformVideoType,
): DestinationPreset {
  return platform in DESTINATION_PRESETS
    ? DESTINATION_PRESETS?.[platform]?.[videoType]
    : DESTINATION_PRESETS.more?.[platform];
}

function getDestinationPresets(
  platform: DestinationPlatform,
): DestinationPreset[] {
  if (platform in DESTINATION_PRESETS) {
    return Object.values(DESTINATION_PRESETS[platform]);
  }

  if (platform in DESTINATION_PRESETS.more) {
    return [DESTINATION_PRESETS.more[platform]];
  }

  return [];
}

function getVideoTypeDescription(
  destinationPreset: DestinationPreset,
  variation: DestinationPresetVariation,
): string {
  const { description, durationMs } = destinationPreset;
  const descriptionValue = description?.[variation];
  const durationValue = durationMs?.[variation];

  if (!durationValue) {
    return descriptionValue;
  }

  if (durationValue) {
    const durationAsSecs = millisToSec(durationValue);
    const isUnderMaxDuration = durationAsSecs <= MAX_PLATFORM_DURATION_AS_SECS;

    const unit = isUnderMaxDuration ? 'sec' : 'min';
    const value = isUnderMaxDuration
      ? durationAsSecs
      : millisToMinutes(durationValue);

    return `(${value} ${unit}) ${descriptionValue}`;
  }

  return '';
}

export function getPlatformWizardConfig(
  platform: DestinationPlatform,
  videoType: PlatformVideoType,
  variation: DestinationPresetVariation,
): VideoTypeWizardSettings {
  const destinationPreset = DESTINATION_PRESETS?.[platform]?.[videoType];

  return {
    destination: DestinationSteps.AUTO_POSTING,
    videoType:
      videoType === 'episode'
        ? VideoTypes.FULL_EPISODE
        : VideoTypes.RANDOM_CLIP,
    clipDuration:
      videoType === 'episode'
        ? null
        : millisToSec(destinationPreset?.durationMs?.[variation]),
    aspectRatioName: destinationPreset?.aspectRatio?.[variation],
  } as VideoTypeWizardSettings;
}

/**
 * Returns the configuration object for a given destination platform, including
 * its video types and related settings.
 *
 * This function aggregates platform-specific configuration, destination presets,
 * and wizard UI settings for each video type supported by the specified platform.
 * It is used to build the UI and logic for selecting and displaying platform-specific
 * video export options.
 *
 * @param {DestinationPlatform} platform - The name of the destination platform.
 * @param {DestinationPresetVariation} [variation='default'] - The variation of the preset.
 * @param {DestinationPresetKey[]} [blacklist=[]] - An array of preset keys to exclude
 * from the returned video types.
 * @returns {PlatformConfig} The configuration object for the platform, including
 * video types, icons, and wizard settings.
 */
export function getPlatformConfig(
  platform: DestinationPlatform,
  variation: DestinationPresetVariation,
  blacklist: DestinationPresetKey[] = [],
): PlatformConfig {
  const { id, icon, videoTypeIconBackground } =
    PLATFORMS_CONFIG?.[platform] ?? {};

  return {
    id,
    icon,
    videoTypes: getDestinationPresets(platform)
      .map(destinationPreset => ({
        id: destinationPreset.type,
        key: destinationPreset.key,
        icon: (
          <VideoTypeIcon
            aspectRatio={destinationPreset.aspectRatio[variation]}
            background={videoTypeIconBackground}
          >
            {destinationPreset?.icon}
          </VideoTypeIcon>
        ),
        title: destinationPreset.title?.[variation],
        description: getVideoTypeDescription(destinationPreset, variation),
        wizardSettings: getPlatformWizardConfig(
          platform,
          destinationPreset.type,
          variation,
        ),
      }))
      .filter(videoType => !blacklist.includes(videoType.key)),
  };
}

/**
 * Returns the configuration object for all audiogram destination platforms.
 *
 * This function aggregates platform-specific configuration for audiogram video generation,
 * combining default platform config, destination-specific settings, and wizard UI settings
 * for each video type supported by each platform.
 *
 * @returns {AudiogramPlatformConfig} An object mapping each destination platform to its
 * configuration, including video types, destination settings, and wizard settings.
 */
export function getAudiogramPlatformsConfig(): AudiogramPlatformConfig {
  const audiogramPlatformsConfig = {} as AudiogramPlatformConfig;

  DESTINATION_PLATFORMS.forEach(platform => {
    const defaultConfig = getPlatformConfig(
      platform as DestinationPlatform,
      'audiogram',
    );

    audiogramPlatformsConfig[platform] = {
      ...defaultConfig,
      videoTypes: defaultConfig.videoTypes.map(videoType => ({
        ...videoType,
        destination:
          AUDIOGRAM_PLATFORMS_DESTINATION_CONFIG[platform][videoType.id],
      })),
    };
  });

  return audiogramPlatformsConfig;
}
